// shared/render-table-tag.ts — client-side port of PHP get_table_columns() + get_random_table()
// (formerly includes/functions.php). The two on-demand endpoints
// (options/api/parse_text_table_inc.php / parse_text_random_table_inc.php) now return DATA; these pure
// functions build the same <table> HTML the PHP builders emitted, running the shared parseText() port on
// each cell. Invoked by ftMountParseTextTables() in parse-text.ts.
//
// parseText is passed IN (not imported) to avoid a parse-text.ts <-> render-table-tag.ts import cycle.
//
// die_icon → the PHP die_icon() already emits a client-mount `<span class="js-die-icon" data-die>`
// rendered by includes/js/die-icon-init.js (auto-run + MutationObserver), so we emit the same span.
// mdi_icon → the PHP mdi_icon() inline-SVG sprite reference, reproduced verbatim.

type Parse = (text: string | null | undefined) => string;

const _g: any = globalThis as any;

function esc(s: unknown): string {
    if (typeof _g.escapeHtml === 'function') return _g.escapeHtml(String(s ?? ''));
    const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s ?? '').replace(/[&<>"']/g, m => map[m] ?? m);
}
function iconMapSafe(key: string): string {
    return typeof _g.iconMap === 'function' ? _g.iconMap(key) : '';
}
function mdiIcon(id: string): string {
    return `<svg class="svg-icon" aria-hidden="true"><use href="#${esc(id)}"></use></svg>`;
}
function baseUrl(): string {
    return (typeof _g.BASE_URL !== 'undefined' && _g.BASE_URL) ? String(_g.BASE_URL) : '/';
}
// Mirror PHP empty(): '', '0', 0, null, undefined, false are all empty. CRITICAL for the roll fields —
// PDO (emulated prepares) returns numeric columns as STRINGS, so a zero modifier arrives as "0", which is
// truthy in JS but empty() in PHP. Without this, every row with roll_*="0" would render a bogus [roll:0d0].
function phpEmpty(v: unknown): boolean {
    return v === null || v === undefined || v === '' || v === '0' || v === 0 || v === false;
}

// ── [table:id] ────────────────────────────────────────────────────────────────────────────────────
export interface TableColumnsData {
    kind: 'table';
    item_name: string;
    columns: string[];   // non-empty header names, in order
    rows: string[][];    // each row = non-empty cell texts, in column order
}

export function renderTableColumns(d: TableColumnsData, parse: Parse): string {
    const title = `<div class='text-center fw-bold mt-3 border-top border-left border-right bg-light text-dark rounded-top'>${esc(d.item_name)}</div>`;
    let thead = '';
    for (const name of d.columns || []) thead += `<th>${parse(name)}</th>`;
    let body = '';
    for (const row of d.rows || []) {
        let tds = '';
        for (const cell of row) tds += `<td>${parse(cell)}</td>`;
        body += `<tr>${tds}</tr>`;
    }
    return `${title}<table class='table table-bordered table-striped table-hover mb-2'><thead><tr>${thead}</tr></thead><tbody>${body}</tbody></table>`;
}

// ── [random_table:id] ───────────────────────────────────────────────────────────────────────────────
export interface RandomTableRow {
    range_begin: number | string;
    range_end: number | string;
    effect: string;
    effect_description: string;
    item_id_attached: string;
    attached_name: string;
    roll_dice: number | string;
    roll_die: number | string;
    roll_mod: number | string;
    range_i: number | string;
}
export interface RandomTableData {
    kind: 'random_table';
    item_name: string;
    item_id: string;
    format: string;
    is_admin: boolean;
    edit_url: string;
    die: number | string;          // die size for the roll button (max roll_range_end of the top row)
    authored_roll_pools?: Array<{ dice: number; die: number; mod: number }>;
    schema: 0 | 1 | 2 | 3;          // 1=effect+desc, 2=effect, 3=desc, 0=degenerate
    rows: RandomTableRow[];
}

function authoredRollNotation(pools: RandomTableData['authored_roll_pools']): string {
    const valid = (pools || []).filter(pool => Number(pool.dice) > 0 && Number(pool.die) > 0);
    if (valid.length === 0) return '';

    // Matching die sizes are one additive expression: 1d100 + 1d100 is conventionally 2d100.
    const dieSizes = new Set(valid.map(pool => Number(pool.die)));
    if (dieSizes.size === 1) {
        const dice = valid.reduce((sum, pool) => sum + Number(pool.dice), 0);
        const die = Number(valid[0]?.die || 0);
        const mod = valid.reduce((sum, pool) => sum + Number(pool.mod || 0), 0);
        return `${dice}d${die}${mod > 0 ? `+${mod}` : (mod < 0 ? String(mod) : '')}`;
    }

    return valid.map(pool => {
        const mod = Number(pool.mod || 0);
        return `${Number(pool.dice)}d${Number(pool.die)}${mod > 0 ? `+${mod}` : (mod < 0 ? String(mod) : '')}`;
    }).join(' + ');
}

export function renderRandomTable(d: RandomTableData, parse: Parse): string {
    const edit = (d.is_admin && d.edit_url)
        ? `<a class='ml-2' href='${esc(d.edit_url)}'>${iconMapSafe('edit')}</a>`
        : '';
    const title = `<div class='text-center fw-bold border-top border-left border-right rounded-top pt-2 pb-2 mt-2'>${esc(d.item_name)}${edit}</div>`;

    const rollNotation = authoredRollNotation(d.authored_roll_pools);
    const rollIcon = rollNotation !== ''
        ? `<span class="badge bg-dark">${esc(rollNotation)}</span>`
        : `<span class="js-die-icon" data-die="${esc(String(d.die))}"></span>`;
    // Lane N2 RT-3 (2026-10-01): the browser rolls the table (websocket/roll-truth.ts); useAbility() only on a page without that bundle.
    const rollBtn = `<button type='button' class='btn btn-primary' data-ft-random-table-roll='${esc(d.item_id)}' onclick="(window.ftRollRandomTableItem || useAbility)('${esc(d.item_id)}');">${rollIcon}</button>`;
    const dropHdr = d.format === 'show_user_select_dropdowns'
        ? `<th class='text-center align-middle' style='width: 15%'>${mdiIcon('format-list-checks')}</th>`
        : '';

    let thead = '<tbody>';
    if (d.schema === 1) {
        thead = `<thead><tr><th class='text-center align-middle'>${rollBtn}</th>${dropHdr}<th class='align-middle w-auto'>Effect</th><th class='align-middle w-100'>Description</th></tr></thead><tbody>`;
    } else if (d.schema === 2) {
        thead = `<thead><tr><th class='text-center align-middle'>${rollBtn}</th>${dropHdr}<th class='align-middle w-100'>Effect</th></tr></thead><tbody>`;
    } else if (d.schema === 3) {
        thead = `<thead><tr><th class='text-center align-middle'>${rollBtn}</th>${dropHdr}<th class='align-middle w-100'>Description</th></tr></thead><tbody>`;
    }

    let body = '';
    for (const r of d.rows || []) {
        const ranges = String(r.range_begin) === String(r.range_end)
            ? String(r.range_begin)
            : `${r.range_begin} - ${r.range_end}`;

        // Effect: an attached item renders as a link (no tag parse needed); otherwise parse the raw text.
        const effectHtml = r.item_id_attached
            ? `<a target='_blank' class='link-primary' href='${baseUrl()}item.php?id=${esc(r.item_id_attached)}'>${esc(r.attached_name)}</a>`
            : parse(r.effect);
        const descHtml = parse(r.effect_description);

        let additional = '';
        if ((!phpEmpty(r.roll_dice) && !phpEmpty(r.roll_die)) || !phpEmpty(r.roll_mod)) {
            const mod = phpEmpty(r.roll_mod) ? '' : `${Number(r.roll_mod) > 0 ? '+' : ''}${r.roll_mod}`;
            const tag = `[roll:${r.roll_dice}d${r.roll_die}${mod}]`;
            additional = `<span class='mr-2'>${parse(tag)}</span>`;
        }

        const formWidget = d.format === 'show_user_select_dropdowns'
            ? `<td align='center'><div class='form-check form-switch'><input class='form-check-input' type='checkbox' id='input_random_table_${esc(d.item_id)}_${esc(r.range_i)}' name='random_table[${esc(d.item_id)}]' value='${esc(r.range_i)}'></div></td>`
            : '';

        if (d.schema === 1) {
            body += `<tr><td class='align-middle text-center text-nowrap'>${esc(ranges)}</td>${formWidget}<td class='align-middle text-left'>${effectHtml}</td><td>${additional}${descHtml}</td></tr>`;
        } else if (d.schema === 2) {
            body += `<tr><td class='align-middle text-center text-nowrap'>${esc(ranges)}</td>${formWidget}<td class='align-middle text-left'>${additional}${effectHtml}</td></tr>`;
        } else if (d.schema === 3) {
            body += `<tr><td class='align-middle text-center text-nowrap'>${esc(ranges)}</td>${formWidget}<td>${additional}${descHtml}</td></tr>`;
        }
    }

    return `${title}<table class='table table-striped table-hover table-sm table-bordered'>${thead}${body}</tbody></table>`;
}
